import { Paper, Typography, styled } from '@mui/material';
import PropTypes from 'prop-types';
import React, { forwardRef, memo } from 'react';
import useDragOperations from '../hooks/useDragOperations';

const packageInitials = (name = '') => {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

const StyledPaper = styled(Paper, {
  shouldForwardProp: (prop) => prop !== 'collapsed',
})(({ theme, collapsed }) => ({
  marginBottom: 0,
  padding: collapsed ? theme.spacing(0.65, 0.4) : theme.spacing(0.75, 0.9),
  cursor: 'grab',
  width: '100%',
  minHeight: collapsed ? 36 : undefined,
  display: collapsed ? 'flex' : 'block',
  alignItems: collapsed ? 'center' : undefined,
  justifyContent: collapsed ? 'center' : undefined,
  transition: 'transform 0.2s ease, background 0.2s ease, box-shadow 0.2s ease',
  background: 'transparent',
  backdropFilter: 'blur(5px)',
  border: '1px solid transparent',
  borderRadius: collapsed ? 8 : 6,
  boxShadow: 'none',
  '&:hover': {
    cursor: 'grabbing',
    transform: 'none',
    boxShadow: 'none',
    background: 'var(--canvas-primary-soft)',
  },
  '&:active': {
    cursor: 'grabbing',
    transform: 'translateY(0)',
  },
}));

const DraggableItem = forwardRef(({ packageData, collapsed = false, onAdd }, ref) => {
  const { handleDragStart } = useDragOperations();

  return (
    <StyledPaper
      ref={ref}
      collapsed={collapsed}
      component="button"
      type="button"
      aria-label={`Add ${packageData.name}`}
      onClick={onAdd}
      draggable
      onDragStart={handleDragStart(packageData)}
    >
      {collapsed ? (
        <Typography
          aria-hidden
          sx={{
            color: 'var(--canvas-text)',
            fontWeight: 700,
            fontSize: '0.68rem',
            lineHeight: 1,
            letterSpacing: '0.04em',
            userSelect: 'none',
          }}
        >
          {packageInitials(packageData.name)}
        </Typography>
      ) : (
        <>
          <Typography
            sx={{
              color: 'var(--canvas-text)',
              fontWeight: 600,
              fontSize: '0.72rem',
              lineHeight: 1.25,
              letterSpacing: '0.01em',
            }}
          >
            {packageData.name}
          </Typography>
          <Typography
            sx={{
              color: 'var(--canvas-muted)',
              fontSize: '0.62rem',
              lineHeight: 1.25,
              mt: 0.2,
            }}
          >
            {packageData.type}
          </Typography>
        </>
      )}
    </StyledPaper>
  );
});

DraggableItem.displayName = 'DraggableItem';

DraggableItem.propTypes = {
  packageData: PropTypes.shape({
    name: PropTypes.string.isRequired,
    type: PropTypes.string.isRequired,
  }).isRequired,
  collapsed: PropTypes.bool,
};

export default memo(DraggableItem);
